import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import type { PlanningEntityIdentifier, PlanningEntityType } from 'generated';

import type { Event } from '../types';

/**
 * The payload a slot hands to `handleDragEnd`. `start` is the only field that
 * varies within a column: it is the moment the dropped appointment is planned
 * at, and it comes from the slot, never from the card next to it.
 */
export type SlotColumnIdentity = {
  scheduleStart: string;
  planningEntityType: PlanningEntityType;
  planningEntityId: string;
  planningEntityIdentifier: PlanningEntityIdentifier;
};

// Type aliases rather than interfaces: Pragmatic's drop data is a
// `Record<string | symbol, unknown>`, and only an alias gets the implicit index
// signature that makes it assignable to one.
export type SlotDropData = SlotColumnIdentity & { start: string };

/**
 * A slot a card's edge delegates to: the payload to drop with, and the key of
 * the element that shows the indicator for it.
 */
export interface SlotDropDelegate {
  slotKey: string;
  data: SlotDropData;
}

/** Marks a slot element that has a drop target registered on it. */
export const DROP_SLOT_ATTRIBUTE = 'data-drop-slot';

/**
 * Carries the element to show the drop feedback on through the drop data, so
 * `handleDragEnd` can anchor the frozen drop preview to the slot the card
 * delegated to rather than to the card itself. A symbol so it cannot collide
 * with — or be mistaken for — a field of the request payload.
 */
export const SLOT_ELEMENT = Symbol('slotElement');

/**
 * The element a drop should be anchored to: the slot a card delegated to, or
 * the drop target itself for a slot. Lives next to `SLOT_ELEMENT` so the cast
 * off the symbol sits where the symbol is defined.
 */
export const resolveDropElement = (dropTarget: {
  data: Record<string | symbol, unknown>;
  element: Element;
}): HTMLElement | undefined =>
  ((dropTarget.data[SLOT_ELEMENT] as HTMLElement | null) ??
    dropTarget.element) as HTMLElement | undefined;

/**
 * The slot a card's resolved edge points at.
 *
 * The fallback matters at the ends of a column: a card there has a delegate on
 * one side only, that side is the sole allowed edge, and so it is the only
 * answer — but `extractClosestEdge` is typed as nullable, and a null edge must
 * not silently become "below".
 */
export const chooseDelegate = (
  edge: Edge | null,
  above?: SlotDropDelegate,
  below?: SlotDropDelegate
): SlotDropDelegate | undefined =>
  (edge === 'top' ? above : edge === 'bottom' ? below : undefined) ??
  above ??
  below;

const SLOT_CATEGORIES: Event['category'][] = ['DROPZONE', 'FREE', 'TRAVELTIME'];

const isSlot = (event: Event | undefined): event is Event =>
  !!event && SLOT_CATEGORIES.includes(event.category);

/**
 * Whether a slot registers a drop target at all. Only travel time opts out: the
 * one following free time is marked `droppable: false` by `addDropzones`,
 * because that free time already covers the same moment.
 */
export const isDroppableSlot = (event: Event | undefined): boolean =>
  isSlot(event) && (event.category !== 'TRAVELTIME' || !!event.droppable);

/**
 * The slot a card's top (`-1`) or bottom (`+1`) edge means, skipping a
 * non-droppable travel time to reach the free time behind it — the same
 * outcome the travel time's `pointer-events: none` produces during a drag.
 *
 * Stops at anything that is not a slot, so a card at the head or tail of a
 * column simply has no delegate on that side. `addDropzones` puts a slot on
 * both sides of every card, so in practice that only happens once the walk has
 * already left the list.
 */
export const findAdjacentSlot = (
  events: Event[],
  index: number,
  direction: -1 | 1
): Event | undefined => {
  for (let i = index + direction; i >= 0 && i < events.length; i += direction) {
    const event = events[i];
    if (!isSlot(event)) return undefined;
    if (isDroppableSlot(event)) return event;
  }
  return undefined;
};

/**
 * The drop payload for one slot. Shared by the slots themselves and by the
 * cards that delegate to them, so a card can never post a different request
 * than the slot it points at.
 */
export const buildSlotDropData = (
  slotStart: string,
  column: SlotColumnIdentity
): SlotDropData => ({
  scheduleStart: column.scheduleStart,
  start: slotStart,
  planningEntityType: column.planningEntityType,
  planningEntityId: column.planningEntityId,
  planningEntityIdentifier: column.planningEntityIdentifier
});

/**
 * Whether a drag carries this board's contract. `handleDragStart` and
 * `handleDragEnd` already ignore anything else; checking it in `canDrop` too
 * keeps a foreign draggable from lighting up slots and card edges on its way
 * across the board.
 */
export const isPlanningCardSource = (data: unknown): boolean => {
  const source = data as { id?: unknown; planningEntityId?: unknown } | null;
  return !!source?.id && !!source?.planningEntityId;
};
